/**
 * BABYWELL THEME — theme.js
 * Mobile nav · Cart drawer · Sticky header · Scroll-reveal
 */

'use strict';

/* ─────────────────────────────────────────────
   Utility helpers
───────────────────────────────────────────── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

function lockScroll() {
  document.documentElement.style.overflow = 'hidden';
}
function unlockScroll() {
  document.documentElement.style.overflow = '';
}

/* ─────────────────────────────────────────────
   1. Sticky Header — shadow on scroll
───────────────────────────────────────────── */
function initStickyHeader() {
  const header = $('.site-header');
  if (!header) return;

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 10);
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });
}

/* ─────────────────────────────────────────────
   2. Mobile Navigation Drawer
───────────────────────────────────────────── */
function initMobileNav() {
  const overlay  = $('.mobile-nav-overlay');
  const drawer   = $('.mobile-nav-drawer');
  const openBtn  = $('#menu-toggle');
  const closeBtn = $('.mobile-nav-drawer__close');

  if (!drawer) return;

  function openNav() {
    overlay?.classList.add('is-open');
    drawer.classList.add('is-open');
    openBtn?.setAttribute('aria-expanded', 'true');
    lockScroll();
    closeBtn?.focus();
  }

  function closeNav() {
    overlay?.classList.remove('is-open');
    drawer.classList.remove('is-open');
    openBtn?.setAttribute('aria-expanded', 'false');
    unlockScroll();
    openBtn?.focus();
  }

  on(openBtn,  'click', openNav);
  on(closeBtn, 'click', closeNav);
  on(overlay,  'click', closeNav);

  // Escape key
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeNav();
  });
}

/* ─────────────────────────────────────────────
   3. Cart Drawer
───────────────────────────────────────────── */
class CartDrawer {
  constructor() {
    this.overlay  = $('.cart-drawer-overlay');
    this.drawer   = $('.cart-drawer');
    this.closeBtn = $('.cart-drawer__close');
    this.subtotalEl = $('.cart-drawer__subtotal-amount');
    this.countEls = $$('.cart-count');
    this.bodyEl   = $('.cart-drawer__body');

    this._bindEvents();
  }

  _bindEvents() {
    // Open triggers
    on(document, 'click', (e) => {
      const trigger = e.target.closest('[data-cart-open]');
      if (trigger) {
        e.preventDefault();
        this.open();
      }
    });

    on(this.closeBtn, 'click', () => this.close());
    on(this.overlay,  'click', () => this.close());

    on(document, 'keydown', (e) => {
      if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.close();
    });

    // Listen for ATC forms
    on(document, 'submit', async (e) => {
      const form = e.target.closest('form[data-atc-form]');
      if (!form) return;
      e.preventDefault();
      await this._addToCart(form);
    });

    // Remove line item
    on(document, 'click', async (e) => {
      const btn = e.target.closest('[data-remove-item]');
      if (!btn) return;
      const key = btn.dataset.removeItem;
      await this._updateItem(key, 0);
    });
  }

  open() {
    this.overlay?.classList.add('is-open');
    this.drawer?.classList.add('is-open');
    lockScroll();
    this.closeBtn?.focus();
    this._fetchAndRender();
  }

  close() {
    this.overlay?.classList.remove('is-open');
    this.drawer?.classList.remove('is-open');
    unlockScroll();
  }

  async _fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async _addToCart(form) {
    const data = new FormData(form);
    const btn  = form.querySelector('[type=submit]');
    btn?.classList.add('btn--loading');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: data,
      });

      if (!res.ok) throw new Error('Add to cart failed');
      this.open();
    } catch (err) {
      console.error(err);
    } finally {
      btn?.classList.remove('btn--loading');
    }
  }

  async _updateItem(key, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    this._fetchAndRender();
  }

  async _fetchAndRender() {
    try {
      const cart = await this._fetchCart();
      this._renderBody(cart);
      this._updateCount(cart.item_count);
      this._updateSubtotal(cart.total_price);
    } catch (err) {
      console.error(err);
    }
  }

  _updateCount(count) {
    this.countEls.forEach(el => {
      el.textContent = count > 0 ? (count > 9 ? '9+' : count) : '';
      el.dataset.count = count;
    });
  }

  _updateSubtotal(cents) {
    if (!this.subtotalEl) return;
    const formatted = (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: window.Babywell?.currency || 'USD',
    });
    this.subtotalEl.textContent = formatted;
  }

  _renderBody(cart) {
    if (!this.bodyEl) return;

    if (cart.item_count === 0) {
      this.bodyEl.innerHTML = `
        <div class="cart-drawer__empty">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007z"/>
          </svg>
          <p>Your cart is empty.</p>
          <a href="/collections/all" class="btn btn--primary btn--sm">Shop Now</a>
        </div>`;
      return;
    }

    const lines = cart.items.map(item => `
      <div class="cart-line">
        <div class="cart-line__image">
          <img src="${item.image ? item.image.replace('_1024x1024', '_240x240') : ''}"
               alt="${this._escape(item.title)}" loading="lazy" width="80" height="80">
        </div>
        <div class="cart-line__info">
          <p class="cart-line__title">${this._escape(item.product_title)}</p>
          ${item.variant_title ? `<p class="cart-line__variant">${this._escape(item.variant_title)}</p>` : ''}
          <p class="cart-line__price">${this._formatMoney(item.final_line_price)} × ${item.quantity}</p>
          <button class="cart-line__remove" data-remove-item="${item.key}" aria-label="Remove ${this._escape(item.title)}">Remove</button>
        </div>
      </div>`).join('');

    this.bodyEl.innerHTML = lines;
  }

  _formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: window.Babywell?.currency || 'USD',
    });
  }

  _escape(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }
}

/* ─────────────────────────────────────────────
   4. Scroll-reveal (Intersection Observer)
───────────────────────────────────────────── */
function initScrollReveal() {
  const elements = $$('.reveal');
  if (!elements.length || !('IntersectionObserver' in window)) {
    elements.forEach(el => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  elements.forEach(el => observer.observe(el));
}

/* ─────────────────────────────────────────────
   5. Announcement Bar — pause on focus
───────────────────────────────────────────── */
function initAnnouncementBar() {
  const bar   = $('.announcement-bar');
  const track = $('.announcement-bar__track');
  if (!bar || !track) return;

  const closeBtn = bar.querySelector('[data-bar-close]');
  if (closeBtn) {
    on(closeBtn, 'click', () => {
      bar.style.display = 'none';
      try { sessionStorage.setItem('bw-bar-closed', '1'); } catch(e) {}
    });
  }

  if (sessionStorage.getItem('bw-bar-closed')) {
    bar.style.display = 'none';
  }
}

/* ─────────────────────────────────────────────
   6. Testimonials simple slider (CSS scroll-snap + JS prev/next)
───────────────────────────────────────────── */
function initTestimonialSlider() {
  $$('[data-testimonial-slider]').forEach(slider => {
    const track = slider.querySelector('[data-slider-track]');
    const prev  = slider.querySelector('[data-slider-prev]');
    const next  = slider.querySelector('[data-slider-next]');
    if (!track) return;

    let idx = 0;

    function slideTo(i) {
      const cards = track.querySelectorAll('.testimonial-card');
      if (!cards.length) return;
      idx = Math.max(0, Math.min(i, cards.length - 1));
      cards[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    on(prev, 'click', () => slideTo(idx - 1));
    on(next, 'click', () => slideTo(idx + 1));
  });
}

/* ─────────────────────────────────────────────
   7. Quick "add to cart" on product cards (AJAX)
───────────────────────────────────────────── */
function initQuickAtc() {
  on(document, 'click', async (e) => {
    const btn = e.target.closest('[data-quick-atc]');
    if (!btn) return;

    const variantId = btn.dataset.quickAtc;
    if (!variantId) return;

    btn.classList.add('btn--loading');

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      // Open cart drawer
      document.dispatchEvent(new CustomEvent('babywell:cart:open'));
    } catch (err) {
      console.error(err);
    } finally {
      btn.classList.remove('btn--loading');
    }
  });

  // Listen for programmatic open
  on(document, 'babywell:cart:open', () => window._cartDrawer?.open());
}

/* ─────────────────────────────────────────────
   Init everything on DOMContentLoaded
───────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initStickyHeader();
  initMobileNav();
  window._cartDrawer = new CartDrawer();
  initScrollReveal();
  initAnnouncementBar();
  initTestimonialSlider();
  initQuickAtc();
});
